import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import Badge from '../../shared/TailwindComponents/Badge';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import {
  PiPencilLight,
  PiSpinnerLight,
  PiTextAlignLeftLight,
  PiTagLight,
  PiCircleDashedLight,
  PiGlobeLight,
  PiTranslateLight,
  PiCalendarBlankLight,
  PiClockClockwiseLight,
  PiClockCountdownLight,
  PiFlowArrowLight,
  PiSparkleLight,
} from 'react-icons/pi';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { getMatterStatusColor, getStatusDisplay, MatterData } from '../MatterDetails';
import Button from '../../shared/TailwindComponents/Button';
import Input from '../../shared/TailwindComponents/Input';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import Textarea from '../../shared/TailwindComponents/Textarea';
import MatterClients, { MatterClient } from '../MatterClients';
import MatterDocuments from './MatterDocuments';
import Flag from '../../shared/TailwindComponents/Flag';
import { formatDate, formatDateTime } from '../../../utils/functions';
import {
  advanceMatterStage,
  fetchMatterDropdownStages,
  fetchMatterDropdownStatuses,
  editMatter,
  MatterStage
} from '../../../store/actions/Matters/MatterActions';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';

// ── Jurisdiction / language code → full name & flag code mappings ──

const jurisdictionDisplayMap: Record<string, { label: string; flag: string }> = {
  SE: { label: 'Sweden', flag: 'SE' },
  US: { label: 'United States', flag: 'US' },
  UK: { label: 'United Kingdom', flag: 'GB' },
  GB: { label: 'United Kingdom', flag: 'GB' },
  DE: { label: 'Germany', flag: 'DE' },
  FR: { label: 'France', flag: 'FR' },
  NO: { label: 'Norway', flag: 'NO' },
  DK: { label: 'Denmark', flag: 'DK' },
  FI: { label: 'Finland', flag: 'FI' },
  NL: { label: 'Netherlands', flag: 'NL' },
  ES: { label: 'Spain', flag: 'ES' },
  IT: { label: 'Italy', flag: 'IT' },
  PT: { label: 'Portugal', flag: 'PT' },
  CH: { label: 'Switzerland', flag: 'CH' },
  AT: { label: 'Austria', flag: 'AT' },
  BE: { label: 'Belgium', flag: 'BE' },
  IE: { label: 'Ireland', flag: 'IE' },
  CA: { label: 'Canada', flag: 'CA' },
  AU: { label: 'Australia', flag: 'AU' },
  NZ: { label: 'New Zealand', flag: 'NZ' },
  JP: { label: 'Japan', flag: 'JP' },
  CN: { label: 'China', flag: 'CN' },
  IN: { label: 'India', flag: 'IN' },
  BR: { label: 'Brazil', flag: 'BR' },
  MX: { label: 'Mexico', flag: 'MX' },
  PL: { label: 'Poland', flag: 'PL' },
  IS: { label: 'Iceland', flag: 'IS' },
};

const languageDisplayMap: Record<string, { label: string; flag: string }> = {
  en: { label: 'English', flag: 'GB' },
  sv: { label: 'Swedish', flag: 'SE' },
  de: { label: 'German', flag: 'DE' },
  fr: { label: 'French', flag: 'FR' },
  no: { label: 'Norwegian', flag: 'NO' },
  nb: { label: 'Norwegian', flag: 'NO' },
  da: { label: 'Danish', flag: 'DK' },
  fi: { label: 'Finnish', flag: 'FI' },
  nl: { label: 'Dutch', flag: 'NL' },
  es: { label: 'Spanish', flag: 'ES' },
  it: { label: 'Italian', flag: 'IT' },
  pt: { label: 'Portuguese', flag: 'PT' },
  ja: { label: 'Japanese', flag: 'JP' },
  zh: { label: 'Chinese', flag: 'CN' },
  ko: { label: 'Korean', flag: 'KR' },
  ar: { label: 'Arabic', flag: 'SA' },
  ru: { label: 'Russian', flag: 'RU' },
  pl: { label: 'Polish', flag: 'PL' },
  is: { label: 'Icelandic', flag: 'IS' },
  // Full-word fallbacks
  english: { label: 'English', flag: 'GB' },
  swedish: { label: 'Swedish', flag: 'SE' },
  german: { label: 'German', flag: 'DE' },
  french: { label: 'French', flag: 'FR' },
  norwegian: { label: 'Norwegian', flag: 'NO' },
  danish: { label: 'Danish', flag: 'DK' },
  finnish: { label: 'Finnish', flag: 'FI' },
  dutch: { label: 'Dutch', flag: 'NL' },
  spanish: { label: 'Spanish', flag: 'ES' },
  italian: { label: 'Italian', flag: 'IT' },
  portuguese: { label: 'Portuguese', flag: 'PT' },
  japanese: { label: 'Japanese', flag: 'JP' },
  chinese: { label: 'Chinese', flag: 'CN' },
  korean: { label: 'Korean', flag: 'KR' },
  arabic: { label: 'Arabic', flag: 'SA' },
  russian: { label: 'Russian', flag: 'RU' },
  polish: { label: 'Polish', flag: 'PL' },
  icelandic: { label: 'Icelandic', flag: 'IS' },
};

function getJurisdictionInfo(raw: string | null | undefined): { label: string; flag: string | null; code: string } | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  const mapped = jurisdictionDisplayMap[code];
  if (mapped) return { label: mapped.label, flag: mapped.flag, code };
  // Fallback: use raw value as label, try code as flag
  return { label: raw, flag: code.length === 2 ? code : null, code };
}

function getLanguageInfo(raw: string | null | undefined): { label: string; flag: string | null; code: string } | null {
  if (!raw) return null;
  const key = raw.trim().toLowerCase();
  const mapped = languageDisplayMap[key];
  if (mapped) return { label: mapped.label, flag: mapped.flag, code: key };
  // Try uppercase for jurisdiction-style codes
  const upperMapped = jurisdictionDisplayMap[raw.trim().toUpperCase()];
  if (upperMapped) return { label: upperMapped.label, flag: upperMapped.flag, code: raw.trim().toUpperCase() };
  return { label: raw, flag: null, code: key };
}

// ── Component ──

interface DetailsTabContentProps {
  matterData: MatterData | null;
  userType: string;
  userAccessLevel?: 'edit' | 'admin' | 'view';
  isEditingMatterInfo: boolean;
  setIsEditingMatterInfo: (editing: boolean) => void;
  editedMatterFields: any;
  setEditedMatterFields: (fields: any) => void;
  handleSaveMatterInfo: () => Promise<void>;
  matterStatuses?: Array<{ label: string; value: string }>;
  onUpdate?: () => void;
  clients?: MatterClient[];
  hasDocuments?: boolean;
}

const DetailsTabContent: React.FC<DetailsTabContentProps> = ({
  matterData,
  userType,
  userAccessLevel = 'view',
  isEditingMatterInfo,
  setIsEditingMatterInfo,
  editedMatterFields,
  setEditedMatterFields,
  handleSaveMatterInfo,
  matterStatuses = [],
  onUpdate,
  clients = [],
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';

  const matterUuid = matterData?.uuid || '';
  const { isGenerating: isGeneratingDescription, handleGenerateMore: handleGenerateDescription, progress: descriptionProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'description',
    onComplete: onUpdate ?? (() => {}),
  });

  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [stages, setStages] = useState<MatterStage[]>([]);
  const [statuses, setStatuses] = useState<Array<{ id?: number; status_id?: number; name: string }>>([]);
  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [stagesLoading, setStagesLoading] = useState(true);
  const [statusesLoading, setStatusesLoading] = useState(true);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!editedMatterFields.MatterName?.trim()) {
      errors.MatterName = t('Matter name is required');
    } else if (editedMatterFields.MatterName.length > 100) {
      errors.MatterName = t('Matter name must be less than 100 characters');
    }
    if (!editedMatterFields.MatterType?.trim()) {
      errors.MatterType = t('Matter type is required');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field: string, value: string) => {
    setEditedMatterFields((prev: any) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const optimisticData = {
        ...matterData,
        MatterName: editedMatterFields.MatterName,
        MatterType: editedMatterFields.MatterType,
        Description: editedMatterFields.Description,
      };
      setEditedMatterFields(optimisticData);
      await handleSaveMatterInfo();
      setIsEditingMatterInfo(false);
      setFormErrors({});
    } catch (error) {
      console.error('Failed to save matter:', error);
      setEditedMatterFields(matterData || {});
      setFormErrors({ general: t('Failed to save changes. Please try again.') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditingMatterInfo(false);
    setFormErrors({});
    setEditedMatterFields(matterData || {});
  };

  // Load stages filtered by the matter's category_id, and statuses
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        setStagesLoading(true);
        const categoryId = matterData?.category_id;
        const stagesResult = await dispatch(fetchMatterDropdownStages(categoryId));
        if (stagesResult && Array.isArray(stagesResult)) {
          setStages(stagesResult);
        }
      } catch (error) {
        console.error('Failed to load stages:', error);
      } finally {
        setStagesLoading(false);
      }

      try {
        setStatusesLoading(true);
        const statusesResult = await dispatch(fetchMatterDropdownStatuses());
        if (statusesResult && Array.isArray(statusesResult)) {
          setStatuses(statusesResult);
        }
      } catch (error) {
        console.error('Failed to load statuses:', error);
      } finally {
        setStatusesLoading(false);
      }
    };
    loadDropdowns();
  }, [dispatch, matterData?.category_id]);

  const handleStageUpdate = async (stageId: number) => {
    if (!matterData?.uuid) return;
    setIsUpdatingStage(true);
    try {
      const result = await dispatch(advanceMatterStage(matterData.uuid, stageId));
      if (result && onUpdate) { onUpdate(); }
    } catch (error: any) {
      console.error('Failed to update stage:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to update stage. Please try again.'),
        modalType: 'error',
        duration: 3000
      }));
    } finally {
      setIsUpdatingStage(false);
    }
  };

  const handleStatusUpdate = async (statusId: number) => {
    if (!matterData?.uuid) return;
    setIsUpdatingStatus(true);
    try {
      const result = await dispatch(editMatter(matterData.uuid, { status_id: statusId }));
      if (result && onUpdate) { onUpdate(); }
    } catch (error: any) {
      console.error('Failed to update status:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to update status. Please try again.'),
        modalType: 'error',
        duration: 3000
      }));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Derived display info for jurisdiction and language
  const jurisdictionRaw = matterData?.Jurisdiction || (matterData as any)?.jurisdiction;
  const languageRaw = (matterData as any)?.language || matterData?.Language;
  const jurisdictionInfo = useMemo(() => getJurisdictionInfo(jurisdictionRaw), [jurisdictionRaw]);
  const languageInfo = useMemo(() => getLanguageInfo(languageRaw), [languageRaw]);

  const dueDateInfo = useMemo(() => {
    const raw = (matterData as any)?.due_date || (matterData as any)?.DueDate;
    if (!raw) return null;
    const now = new Date();
    const due = new Date(raw);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffHours / 24;

    if (diffMs < 0) {
      return { text: t('Overdue'), urgency: 'overdue' as const };
    } else if (diffHours <= 48) {
      return { text: t('Due Soon'), urgency: 'soon' as const };
    } else if (diffDays <= 7) {
      return { text: t('This Week'), urgency: 'upcoming' as const };
    }
    return null;
  }, [(matterData as any)?.due_date, (matterData as any)?.DueDate, t]);

  const createdByName = useMemo(() => {
    if (!matterData?.collaborators?.length) return null;
    const creator = matterData.collaborators.find((c: any) => c.role === 'creator');
    return creator?.name || null;
  }, [matterData?.collaborators]);

  if (!matterData) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-4">
        <div className="lg:col-span-2 space-y-5">
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-36 rounded-xl"></div>
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-24 rounded-xl"></div>
        </div>
        <div className="space-y-5">
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-48 rounded-xl"></div>
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-32 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const dueDateRaw = (matterData as any).due_date || (matterData as any).DueDate;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-4">

      {/* ── Left Column ── */}
      <div className="lg:col-span-2 space-y-5">

        {/* ── Description Card ── */}
        <div data-tour-id="details-description-card" className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <PiTextAlignLeftLight className="h-4 w-4" />
              <span className="text-xs font-medium">{t('Description')}</span>
            </div>
            {canEdit && !isEditingMatterInfo && !isGeneratingDescription && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setIsEditingMatterInfo(true)}
                  icon={<PiPencilLight className="h-3.5 w-3.5" />}
                >
                  {t('Edit')}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleGenerateDescription}
                  disabled={!hasDocuments}
                  tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                  icon={<PiSparkleLight className="h-3.5 w-3.5" />}
                >
                  {!matterData?.Description ? t('Generate') : t('Regenerate')}
                </Button>
              </div>
            )}
          </div>

          {isGeneratingDescription || descriptionProgress.status === 'complete' || descriptionProgress.status === 'failed' ? (
            <FindMoreProgress progress={descriptionProgress} isGenerating={isGeneratingDescription} />
          ) : isEditingMatterInfo && canEdit ? (
            <div className="p-5 space-y-4">
              <Input
                type="text"
                label={t('Matter Name')}
                placeholder={t('Enter matter name')}
                value={editedMatterFields.MatterName || ''}
                onChange={(e) => handleFieldChange('MatterName', e.target.value)}
                error={formErrors.MatterName}
                required
                infoText={t('The unique name for this legal matter')}
                className="w-full"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  type="text"
                  label={t('Matter Type')}
                  placeholder={t('Enter matter type, e.g., Breach of Contract')}
                  value={editedMatterFields.MatterType || ''}
                  onChange={(e) => handleFieldChange('MatterType', e.target.value)}
                  error={formErrors.MatterType}
                  required
                  infoText={t('The category or type of legal matter')}
                  className="w-full"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('Status')}
                  </label>
                  {statusesLoading ? (
                    <div className="flex items-center gap-2 p-2">
                      <PiSpinnerLight className="h-4 w-4 animate-spin text-primary-600" />
                      <span className="text-sm text-gray-500">{t('Loading...')}</span>
                    </div>
                  ) : statuses.length > 0 ? (
                    <Dropdown
                      options={statuses.map(status => ({
                        value: (status.id || status.status_id)?.toString() || '',
                        label: status.name,
                      }))}
                      value={(matterData?.status_id || matterData?.current_status_id || '').toString()}
                      onChange={(value) => {
                        const statusId = Array.isArray(value) ? parseInt(value[0], 10) : parseInt(value, 10);
                        if (!isNaN(statusId)) {
                          handleStatusUpdate(statusId);
                        }
                      }}
                      placeholder={t('Select a status')}
                      className="w-full"
                    />
                  ) : (
                    <p className="text-sm text-gray-500">{matterData?.status_name || t('No status assigned')}</p>
                  )}
                </div>
              </div>

              <Textarea
                label={t('Description')}
                placeholder={t('Provide a detailed description of the matter')}
                value={editedMatterFields.Description || matterData.Description || ''}
                onChange={(e) => handleFieldChange('Description', e.target.value)}
                error={formErrors.Description}
                description={t('Optional: Add details about the matter for future reference')}
                className="w-full"
                id="description"
              />

              {dueDateRaw && (
                <Input
                  type="date"
                  label={t('Due Date')}
                  value={(editedMatterFields as any).due_date || dueDateRaw ? new Date(dueDateRaw).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFieldChange('due_date', e.target.value)}
                  infoText={t('The due date for this matter')}
                  className="w-full"
                />
              )}

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                <Button variant="neutral" size="small" onClick={handleCancel} disabled={isSaving}>
                  {t('Cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSave}
                  disabled={isSaving}
                  icon={isSaving ? <PiSpinnerLight className="h-4 w-4 animate-spin" /> : undefined}
                >
                  {isSaving ? t('Saving...') : t('Save Changes')}
                </Button>
              </div>

              {formErrors.general && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300">{formErrors.general}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="p-5">
              {matterData.Description ? (
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-w-prose">
                  {matterData.Description}
                </p>
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic mb-3">
                    {t('No description provided')}
                  </p>
                  {canEdit && (
                    <Button
                      variant="primary"
                      size="small"
                      onClick={handleGenerateDescription}
                      disabled={!hasDocuments}
                      tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                      icon={<PiSparkleLight className="h-3.5 w-3.5" />}
                      className='mx-auto'
                    >
                      {t('Generate')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Metadata Grid ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

          {/* Type */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <PiTagLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Type')}</span>
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {matterData.MatterType || t('Not set')}
            </p>
          </div>

          {/* Status */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <PiCircleDashedLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Status')}</span>
            </div>
            {matterData?.status_name ? (
              <Badge color={getMatterStatusColor(matterData.status_name, matterStatuses) as BadgeColor}>
                {getStatusDisplay(matterData.status_name, t, matterStatuses)}
              </Badge>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">{t('Not set')}</p>
            )}
          </div>

          {/* Jurisdiction */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <PiGlobeLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Jurisdiction')}</span>
            </div>
            {jurisdictionInfo ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t(jurisdictionInfo.label)}</span>
                {jurisdictionInfo.flag && (
                  <Flag countryCode={jurisdictionInfo.flag} code={jurisdictionInfo.code} size="sm" />
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">{t('Not set')}</p>
            )}
          </div>

          {/* Language */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <PiTranslateLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
              <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Language')}</span>
            </div>
            {languageInfo ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t(languageInfo.label)}</span>
                {languageInfo.flag && (
                  <Flag countryCode={languageInfo.flag} code={languageInfo.code} size="sm" />
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">{t('Not set')}</p>
            )}
          </div>
        </div>

        {/* ── Due Date Card (only if set) ── */}
        {dueDateRaw && (
          <div className={`border rounded-lg shadow-sm p-4 ${
            dueDateInfo?.urgency === 'overdue'
              ? 'border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10'
              : dueDateInfo?.urgency === 'soon'
              ? 'border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/10'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          }`}>
            <div className="flex items-center gap-1.5 mb-2">
              <PiClockCountdownLight className={`h-3.5 w-3.5 ${
                dueDateInfo?.urgency === 'overdue' ? 'text-red-400' :
                dueDateInfo?.urgency === 'soon' ? 'text-amber-400' :
                'text-gray-400 dark:text-gray-500'
              }`} />
              <span className={`text-[11px] font-medium ${
                dueDateInfo?.urgency === 'overdue' ? 'text-red-400' :
                dueDateInfo?.urgency === 'soon' ? 'text-amber-400' :
                'text-gray-400 dark:text-gray-500'
              }`}>{t('Due Date')}</span>
            </div>
            <div className="flex items-center gap-3">
              <p className={`text-sm font-medium ${
                dueDateInfo?.urgency === 'overdue' ? 'text-red-600 dark:text-red-400' :
                dueDateInfo?.urgency === 'soon' ? 'text-amber-600 dark:text-amber-400' :
                'text-gray-700 dark:text-gray-200'
              }`}>
                {formatDate(dueDateRaw)}
              </p>
              {dueDateInfo && (
                <span className={`text-[11px] font-medium ${
                  dueDateInfo.urgency === 'overdue' ? 'text-red-500 dark:text-red-400' :
                  dueDateInfo.urgency === 'soon' ? 'text-amber-500 dark:text-amber-400' :
                  'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {dueDateInfo.text}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Activity Row ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {createdByName && (
            <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
              <PiCalendarBlankLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created By')}</span>
                <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{createdByName}</p>
              </div>
            </div>
          )}

          {matterData?.created_at && (
            <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
              <PiCalendarBlankLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created')}</span>
                <p className="text-sm text-gray-700 dark:text-gray-200">{formatDate(matterData.created_at)}</p>
              </div>
            </div>
          )}

          {matterData?.updated_at && (
            <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
              <PiClockClockwiseLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Last Updated')}</span>
                <p className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(matterData.updated_at)}</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Documents ── */}
        <MatterDocuments matterUuid={matterData?.uuid || ''} />
      </div>

      {/* ── Right Column ── */}
      <div className="space-y-5">

        {/* ── Process & Stage Card ── */}
        <div data-tour-id="details-status-section" className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
            <PiFlowArrowLight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500">{t('Process & Stage')}</span>
          </div>

          <div className="p-4 space-y-4">
            {matterData?.category_name && (
              <div>
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Category')}</span>
                <div className="mt-1.5 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-md border border-gray-100 dark:border-gray-700/60">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{matterData.category_name}</p>
                  {matterData.category_description && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 leading-relaxed">{matterData.category_description}</p>
                  )}
                </div>
              </div>
            )}

            <div>
              <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Stage')}</span>
              {stagesLoading ? (
                <div className="flex items-center gap-2 mt-1.5 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-md border border-gray-100 dark:border-gray-700/60">
                  <PiSpinnerLight className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
                  <span className="text-sm text-gray-500">{t('Loading...')}</span>
                </div>
              ) : canEdit && stages.length > 0 ? (
                <div className="mt-1.5">
                  <Dropdown
                    options={stages.map(stage => ({
                      value: (stage.stage_id || stage.id)?.toString() || '',
                      label: stage.name,
                    }))}
                    value={(matterData?.stage_id || matterData?.current_stage_id || '').toString()}
                    onChange={(value) => {
                      const stageId = Array.isArray(value) ? parseInt(value[0], 10) : parseInt(value, 10);
                      if (!isNaN(stageId)) {
                        handleStageUpdate(stageId);
                      }
                    }}
                    placeholder={t('Select a stage')}
                    className="w-full"
                  />
                </div>
              ) : (
                <div className="mt-1.5 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-md border border-gray-100 dark:border-gray-700/60">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{matterData?.stage_name || t('No stage assigned')}</p>
                </div>
              )}
            </div>

            {!canEdit && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                <p className="text-xs text-blue-600 dark:text-blue-300 leading-relaxed">
                  {t('You have view-only access. Contact an editor to change the stage.')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Clients Card ── */}
        <div data-tour-id="details-clients-section">
          <MatterClients
            matterUuid={matterData?.uuid || ''}
            clients={clients}
            canEdit={canEdit}
            onUpdate={onUpdate}
          />
        </div>
      </div>
    </div>
  );
};

export default DetailsTabContent;
